import type { AiInsightResponse, ContainsGroup, Meal, Symptom, VerifiedFinding } from './types';
import { CONTAINS_GROUP_LABELS, getMealGroups, getMealIngredients, normaliseIngredientName } from './ingredientTaxonomy';
import { buildFlareContext, mealFollowedByFlare, type FlareContext } from './insightStats';

type Finding = AiInsightResponse['findings'][number];

const DAY_MS = 86_400_000;
const GROUP_IDS = Object.keys(CONTAINS_GROUP_LABELS) as ContainsGroup[];

interface MealInfo {
  meal: Meal;
  ms: number;
  names: Set<string>;
  groups: Set<ContainsGroup>;
  /** For each group in the meal, what carries it: raw ingredient names, or "component:<group>". */
  groupCarriers: Map<ContainsGroup, Set<string>>;
}

/** What in this meal carries the item: the ingredient itself, or the ingredients that carry the group. */
function itemCarriers(item: string, info: MealInfo): Set<string> | null {
  const n = normaliseIngredientName(item);
  if (!n) return null;
  if (info.names.has(n)) return new Set([n]);
  for (const g of GROUP_IDS) {
    if ((n === g || n === CONTAINS_GROUP_LABELS[g].toLowerCase()) && info.groups.has(g)) {
      return info.groupCarriers.get(g) ?? new Set([`component:${g}`]);
    }
  }
  return null;
}

function matchesItem(item: string, info: MealInfo): boolean {
  return itemCarriers(item, info) !== null;
}

/**
 * A meal holds all the items only when no single ingredient carries two of them: "gluten" and "soy"
 * in a meal whose soy sauce carries both is explained by one food, not a combination of two.
 */
function matchesAll(items: string[], info: MealInfo): boolean {
  const sets: Set<string>[] = [];
  for (const item of new Set(items.map((i) => normaliseIngredientName(i)))) {
    const c = itemCarriers(item, info);
    if (!c) return false;
    for (const prev of sets) for (const x of c) if (prev.has(x)) return false;
    sets.push(c);
  }
  return true;
}

/** Group -> the raw ingredients (or "component:<group>") that carry it in this meal. */
export function mealGroupCarriers(meal: Meal): Map<ContainsGroup, Set<string>> {
  const carriers = new Map<ContainsGroup, Set<string>>();
  for (const ing of getMealIngredients(meal)) {
    const name = normaliseIngredientName(ing.name);
    for (const g of ing.contains) {
      if (!carriers.has(g)) carriers.set(g, new Set());
      carriers.get(g)!.add(name);
    }
  }
  for (const g of getMealGroups(meal)) if (!carriers.has(g)) carriers.set(g, new Set([`component:${g}`]));
  return carriers;
}

function localDay(ms: number): string {
  const d = new Date(ms);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function dayIndex(ms: number): number {
  const d = new Date(ms);
  return Math.round(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DAY_MS);
}

function applyCondition(condition: string, matching: MealInfo[], symptoms: Symptom[]): MealInfo[] | null {
  const c = condition.toLowerCase();

  const consec = c.match(/(\d+)\+? consecutive days/);
  if (consec) {
    const n = Number(consec[1]);
    const byDay = new Map<number, MealInfo[]>();
    for (const m of matching) {
      const k = dayIndex(m.ms);
      byDay.set(k, [...(byDay.get(k) ?? []), m]);
    }
    // Meals on any day that is at least the Nth day in a row, judged from earlier days only (no look-ahead).
    const result: MealInfo[] = [];
    for (const [day, meals] of byDay) {
      let len = 1;
      while (len < n && byDay.has(day - len)) len++;
      if (len >= n) result.push(...meals);
    }
    return result;
  }

  const after = c.match(/after (\d{1,2})(?::?(\d{2}))?\s*(am|pm)?(?![\w:])/);
  if (after) {
    let hour = Number(after[1]);
    const minute = Number(after[2] ?? 0);
    const mer = after[3];
    if (minute >= 60) return null;
    if (mer) {
      if (hour < 1 || hour > 12) return null;
      if (mer === 'pm' && hour !== 12) hour += 12;
      if (mer === 'am' && hour === 12) hour = 0;
    } else if (hour >= 24) {
      return null;
    }
    const threshold = hour * 60 + minute;
    return matching.filter((m) => {
      const d = new Date(m.ms);
      return d.getHours() * 60 + d.getMinutes() >= threshold;
    });
  }

  if (/within (\d+) ?h of a (bowel movement|bm) gap/.test(c)) {
    const stools = symptoms
      .filter((s) => typeof s.stool_type === 'number')
      .map((s) => new Date(s.timestamp_iso).getTime())
      .filter((t) => Number.isFinite(t))
      .sort((a, b) => a - b);
    return matching.filter((m) => {
      let last: number | null = null;
      for (const t of stools) {
        if (t < m.ms) last = t;
        else break;
      }
      return last !== null && m.ms - last >= DAY_MS;
    });
  }

  return null;
}

function verifyWith(f: Finding, infos: MealInfo[], symptoms: Symptom[], ctx: FlareContext): VerifiedFinding | null {
  if (!f || typeof f !== 'object') return null;
  if (!Array.isArray(f.items) || f.items.length === 0 || !f.items.every((i) => typeof i === 'string')) return null;
  if (!Array.isArray(f.evidenceDates)) return null;
  if (f.condition != null && typeof f.condition !== 'string') return null;
  const condition = f.condition?.trim() || '';
  if (f.kind === 'pattern' && !condition) return null;

  const counted = countWith(f.items, condition, infos, symptoms, ctx);
  if (!counted) return null;
  const { count, flared: flares, flaredDays } = counted;
  if (!f.evidenceDates.some((d) => typeof d === 'string' && flaredDays.includes(d))) return null;
  if (count < 2) return null;

  const usual = `usual ~${Math.round(ctx.baselineRate * 100)}%`;
  const displayText = condition
    ? `${condition.charAt(0).toUpperCase()}${condition.slice(1)}: flared ${flares} of ${count} times · ${usual}`
    : `${count} meals · flared after ${flares} · ${usual}`;
  return { ...f, verifiedCount: count, verifiedFlares: flares, displayText, verifiedBaseline: ctx.baselineRate };
}

export interface PatternCount {
  /** Meals that contained every item (and met the condition, if any). */
  count: number;
  /** How many of those meals were followed by a flare. */
  flared: number;
  /** Sorted local YYYY-MM-DD days of the flaring meals. */
  flaredDays: string[];
}

/** The verifier's own counting: null when an item never appears or the condition is not one the phone can check. */
function countWith(
  items: string[],
  condition: string,
  infos: MealInfo[],
  symptoms: Symptom[],
  ctx: FlareContext
): PatternCount | null {
  // every item must match at least one meal in the whole history
  for (const item of items) {
    if (!infos.some((i) => matchesItem(item, i))) return null;
  }
  let matching = infos.filter((i) => matchesAll(items, i));
  if (condition) {
    const filtered = applyCondition(condition, matching, symptoms);
    if (!filtered) return null;
    matching = filtered;
  }
  const days = new Set<string>();
  let flared = 0;
  for (const m of matching) {
    if (mealFollowedByFlare(m.meal, ctx).flared) {
      flared++;
      days.add(localDay(m.ms));
    }
  }
  return { count: matching.length, flared, flaredDays: [...days].sort() };
}

/**
 * Build a reusable counter over the whole history, using exactly the counting that verifies AI findings.
 * Pass an existing FlareContext to avoid rebuilding it.
 */
export function makePatternCounter(
  meals: Meal[],
  symptoms: Symptom[],
  ctx: FlareContext = buildFlareContext(meals, symptoms)
): (items: string[], condition?: string) => PatternCount | null {
  const infos = toInfos(meals);
  return (items, condition) => countWith(items, condition?.trim() || '', infos, symptoms, ctx);
}

function toInfos(meals: Meal[]): MealInfo[] {
  const infos: MealInfo[] = [];
  for (const meal of meals) {
    const ms = new Date(meal.timestamp_iso).getTime();
    if (!Number.isFinite(ms)) continue;
    infos.push({
      meal,
      ms,
      names: new Set(getMealIngredients(meal).map((i) => normaliseIngredientName(i.name))),
      groups: new Set(getMealGroups(meal)),
      groupCarriers: mealGroupCarriers(meal),
    });
  }
  return infos;
}

export function verifyFinding(f: Finding, meals: Meal[], symptoms: Symptom[]): VerifiedFinding | null {
  return verifyWith(f, toInfos(meals), symptoms, buildFlareContext(meals, symptoms));
}

export function verifyAll(ai: AiInsightResponse, meals: Meal[], symptoms: Symptom[]): VerifiedFinding[] {
  if (!ai || !Array.isArray(ai.findings)) return [];
  const infos = toInfos(meals);
  const ctx = buildFlareContext(meals, symptoms);
  const out: VerifiedFinding[] = [];
  for (const f of ai.findings) {
    try {
      const v = verifyWith(f, infos, symptoms, ctx);
      if (v) out.push(v);
    } catch {
      // one malformed finding must not discard the others
    }
  }
  return out;
}
